import { type ReactNode, useEffect, useRef, useState } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  IconChevronDown,
  IconChevronUp,
} from "@tabler/icons-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { NvpStatusDot, type NvpStatus } from "@/components/nvp-status-dot";
import { TeamFlag } from "@/components/team-flag";
import { ParamDiffTable } from "@/components/param-diff-table";
import { boatSyncKeys } from "@/api/query-keys";
import type { BoatCode } from "@/types/identifiers";
import type { components } from "@/api/generated/schema";

type BoatCompareResponse = components["schemas"]["BoatCompareResponse"];
type BoatWriteResponse = components["schemas"]["BoatWriteResponse"];

interface BoatSyncCardProps {
  boatCode: BoatCode;
  teamName: string;
  isoAlpha2: string;
  nvpStatus: NvpStatus;
  syncType: string;
  /** Cache-key leaf for the compare query — defaults to boatCode. Build-scoped
   * callers (wing cals) pass a build id so per-event compares don't collide. */
  syncId?: string;
  sourceAvailable: boolean;
  unavailableReason?: string;
  sourceLabel?: ReactNode;
  compareFn: (boatCode: BoatCode) => Promise<BoatCompareResponse>;
  writeBackFn: (boatCode: BoatCode) => Promise<BoatWriteResponse>;
  writeBackLabel?: string;
  expectedHeader?: string;
  metadata?: ReactNode;
  onWriteBackSuccess?: () => void;
  acceptFn?: (boatCode: BoatCode) => Promise<unknown>;
  acceptLabel?: string;
  onAcceptSuccess?: () => void;
  /** Wing cals provenance: event the golden was measured at, and the current
   * event the live wing is read at (shown/swapped in the diff header on hover). */
  sourceEventName?: string | null;
  currentEventName?: string | null;
}

export function BoatSyncCard({
  boatCode,
  teamName,
  isoAlpha2,
  nvpStatus,
  syncType,
  syncId,
  sourceAvailable,
  unavailableReason,
  sourceLabel,
  compareFn,
  writeBackFn,
  writeBackLabel = "Sync",
  expectedHeader = "Expected",
  metadata,
  onWriteBackSuccess,
  acceptFn,
  acceptLabel = "Accept",
  onAcceptSuccess,
  sourceEventName,
  currentEventName,
}: BoatSyncCardProps) {
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [confirmArmed, setConfirmArmed] = useState(false);
  const [acceptArmed, setAcceptArmed] = useState(false);
  const [hoverTarget, setHoverTarget] = useState<"source" | "live" | null>(null);
  const confirmTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const acceptTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => () => {
    clearTimeout(confirmTimer.current);
    clearTimeout(acceptTimer.current);
  }, []);

  const online = nvpStatus === "online";
  const enabled = online && sourceAvailable;

  const compareQuery = useQuery({
    queryKey: boatSyncKeys.compare(syncType, syncId ?? boatCode),
    queryFn: () => compareFn(boatCode),
    enabled,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const writeMutation = useMutation({
    mutationFn: () => writeBackFn(boatCode),
    onSuccess: (data) => {
      if (data.error_count > 0) {
        toast.warning(
          `${writeBackLabel} completed with ${data.error_count} error${data.error_count !== 1 ? "s" : ""}`,
        );
      } else {
        toast.success(
          `${writeBackLabel === "Restore" ? "Restored" : "Synced"} ${data.success_count} parameter${data.success_count !== 1 ? "s" : ""}`,
        );
      }
      qc.invalidateQueries({
        queryKey: boatSyncKeys.compare(syncType, syncId ?? boatCode),
      });
      onWriteBackSuccess?.();
    },
    onError: (err) => toast.error(err.message),
  });

  const acceptMutation = useMutation({
    mutationFn: () => acceptFn!(boatCode),
    onSuccess: () => {
      toast.success("Accepted live values as new golden");
      qc.invalidateQueries({
        queryKey: boatSyncKeys.compare(syncType, syncId ?? boatCode),
      });
      onAcceptSuccess?.();
    },
    onError: (err) => toast.error(err.message),
  });

  const result = compareQuery.data;
  const hasSource = result ? result.has_source : sourceAvailable;
  const hasDiffs = result ? result.has_source && !result.matched : false;
  const isMatched = result ? result.has_source && result.matched : false;
  const diffCount = result?.diffs.length ?? 0;
  const busy = compareQuery.isFetching || writeMutation.isPending || acceptMutation.isPending;
  const isOffline = !online;
  const isExpanded = expanded && result && (hasDiffs || (result.errors?.length ?? 0) > 0);

  const borderColor =
    nvpStatus === "online"
      ? "border-l-green-500"
      : nvpStatus === "offline"
        ? "border-l-red-500"
        : "border-l-border";

  return (
    <div>
      {/* Card header */}
      <div
        className={`
          flex flex-col gap-2 sm:flex-row sm:items-center bg-card border-l-3 ${borderColor}
          pl-4 pr-5 py-2.5 sm:py-0 sm:h-14 transition-shadow
          ${isOffline ? "" : "hover:shadow-[0_1px_4px_rgba(0,0,0,0.04)]"}
          ${isExpanded ? "" : "rounded-r-lg"}
        `}
      >
        {/* Left: NVP dot + team info */}
        <div
          className={`flex flex-wrap items-center gap-y-1 flex-1 min-w-0 ${isOffline ? "opacity-45" : ""}`}
        >
          <div className="mr-3.5">
            <NvpStatusDot status={nvpStatus} />
          </div>
          <TeamFlag iso_alpha2={isoAlpha2} name={teamName} size="xs" />
          <span className="font-medium text-[13px] ml-2">{teamName}</span>
          <span className="text-xs font-medium text-muted-foreground/50 ml-2">
            {boatCode}
          </span>
          {sourceLabel && (
            <span className="text-[11px] text-muted-foreground/50 ml-3">
              {sourceLabel}
            </span>
          )}
          {/* Common Cals metadata from compare response */}
          {result?.build_version != null && (
            <>
              <span className="text-[11px] text-muted-foreground/40 mx-2">
                |
              </span>
              <span className="text-[11px] text-muted-foreground">
                BuildNum:{" "}
                <span className="font-medium text-foreground/70 font-mono">
                  {result.build_version}
                </span>
              </span>
            </>
          )}
          {result?.release_name && (
            <>
              <span className="text-[11px] text-muted-foreground/40 mx-2">
                |
              </span>
              <span className="text-[11px] text-muted-foreground">
                Release:{" "}
                <span className="font-medium text-foreground/70">
                  {result.release_name}
                </span>
              </span>
            </>
          )}
          {metadata}
        </div>

        {/* Right: action area */}
        <div
          className={`flex flex-wrap items-center gap-2 shrink-0 ${isOffline && hasSource ? "opacity-45" : ""}`}
        >
          {!hasSource && !compareQuery.isFetching ? (
            <span className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-muted-foreground/25 px-2.5 py-1 text-xs font-medium text-muted-foreground/50">
              {unavailableReason ??
                (result?.errors?.[0] ?? "No source available")}
            </span>
          ) : (
            <>
              {/* Loading state */}
              {compareQuery.isFetching && (
                <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                  <span className="h-3.5 w-3.5 border-2 border-muted-foreground/20 border-t-muted-foreground/60 rounded-full animate-spin" />
                  Checking…
                </span>
              )}

              {writeMutation.isPending && (
                <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                  <span className="h-3.5 w-3.5 border-2 border-muted-foreground/20 border-t-muted-foreground/60 rounded-full animate-spin" />
                  {writeBackLabel === "Restore" ? "Restoring" : "Syncing"}…
                </span>
              )}

              {acceptMutation.isPending && (
                <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                  <span className="h-3.5 w-3.5 border-2 border-muted-foreground/20 border-t-muted-foreground/60 rounded-full animate-spin" />
                  Accepting…
                </span>
              )}

              {/* Matched */}
              {!compareQuery.isFetching &&
                !writeMutation.isPending &&
                isMatched && (
                  <span className="flex items-center gap-1 text-xs font-medium text-green-600 dark:text-green-400">
                    <svg
                      className="h-3.5 w-3.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2.5}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    Matching
                  </span>
                )}

              {/* Diffs */}
              {!compareQuery.isFetching &&
                !writeMutation.isPending &&
                !acceptMutation.isPending &&
                hasDiffs && (
                  <>
                    <button
                      onClick={() => setExpanded(!expanded)}
                      className="flex items-center gap-1 text-xs font-medium text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 transition-colors"
                    >
                      {diffCount} diff{diffCount !== 1 ? "s" : ""}
                      {expanded ? (
                        <IconChevronUp className="h-3 w-3" />
                      ) : (
                        <IconChevronDown className="h-3 w-3" />
                      )}
                    </button>
                    {acceptFn && (
                      acceptArmed ? (
                        <Button
                          size="sm"
                          disabled={busy}
                          onClick={() => {
                            setAcceptArmed(false);
                            clearTimeout(acceptTimer.current);
                            acceptMutation.mutate();
                          }}
                          onMouseEnter={() => setHoverTarget("source")}
                          onMouseLeave={() => setHoverTarget(null)}
                          className="h-8 text-xs font-semibold bg-green-50 dark:bg-green-950/40 text-green-700 dark:text-green-400 border border-green-300 dark:border-green-900 hover:bg-green-100 dark:hover:bg-green-900/40 shadow-none"
                        >
                          Yes!
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          disabled={busy}
                          onClick={() => {
                            setAcceptArmed(true);
                            setConfirmArmed(false);
                            clearTimeout(acceptTimer.current);
                            acceptTimer.current = setTimeout(() => setAcceptArmed(false), 3000);
                          }}
                          onMouseEnter={() => setHoverTarget("source")}
                          onMouseLeave={() => setHoverTarget(null)}
                          className="h-8 text-xs font-semibold bg-green-50 dark:bg-green-950/40 text-green-700 dark:text-green-400 border border-green-300 dark:border-green-900 hover:bg-green-100 dark:hover:bg-green-900/40 shadow-none"
                        >
                          {acceptLabel}
                        </Button>
                      )
                    )}
                    {confirmArmed ? (
                      <Button
                        size="sm"
                        disabled={busy}
                        onClick={() => {
                          setConfirmArmed(false);
                          clearTimeout(confirmTimer.current);
                          writeMutation.mutate();
                        }}
                        onMouseEnter={() => setHoverTarget("live")}
                        onMouseLeave={() => setHoverTarget(null)}
                        className="h-8 text-xs font-semibold bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-400 border border-red-300 dark:border-red-900 hover:bg-red-100 dark:hover:bg-red-900/40 shadow-none"
                      >
                        Confirm {writeBackLabel.toLowerCase()}?
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        disabled={busy}
                        onClick={() => {
                          setConfirmArmed(true);
                          setAcceptArmed(false);
                          clearTimeout(confirmTimer.current);
                          confirmTimer.current = setTimeout(() => setConfirmArmed(false), 3000);
                        }}
                        onMouseEnter={() => setHoverTarget("live")}
                        onMouseLeave={() => setHoverTarget(null)}
                        className="h-8 text-xs font-semibold bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400 border border-amber-300 dark:border-amber-900 hover:bg-amber-100 dark:hover:bg-amber-900/40 shadow-none"
                      >
                        {writeBackLabel}
                      </Button>
                    )}
                  </>
                )}

              {/* No source from compare response (e.g. unknown build version) */}
              {!compareQuery.isFetching &&
                !writeMutation.isPending &&
                result &&
                !result.has_source && (
                  <span className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-muted-foreground/25 px-2.5 py-1 text-xs font-medium text-muted-foreground/50">
                    {result.errors?.[0] ?? "No source available"}
                  </span>
                )}
            </>
          )}
        </div>
      </div>

      {/* Expandable diff detail */}
      {isExpanded && result && (
        <div className="bg-card border-l-3 border-l-amber-500 rounded-br-lg pl-4 pr-5 py-3">
          <ParamDiffTable
            diffs={result.diffs}
            expectedHeader={expectedHeader}
            errors={result.errors}
            writeTarget={hoverTarget}
            sourceEventName={sourceEventName}
            currentEventName={currentEventName}
          />
        </div>
      )}
    </div>
  );
}
